import { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
} from "../../../../components";
import { Divider, Skeleton } from "@mui/material";
import { OrdemServicoService } from "../../../../services";
import { responseErros } from "../../../../utils";

const ordemServicoService = new OrdemServicoService();

const defaultData = {
  notaMedia: 0,
  indiceSatisfacao: 0,
  enviadas: 0,
  respondidas: 0,
  taxaResposta: 0,
  comparativo: null,
};

function Estrelas({ nota }) {
  const cheia = Math.floor(nota);
  const temMeia = nota - cheia >= 0.25 && nota - cheia < 0.75;
  const vazia = 5 - cheia - (temMeia ? 1 : 0);
  return (
    <Box display="flex" alignItems="center" gap={0.5} mt={1}>
      {Array.from({ length: cheia }).map((_, i) => (
        <i
          key={`c${i}`}
          className="ph-fill ph-star"
          style={{ color: "#f59e0b", fontSize: 22 }}
        />
      ))}
      {temMeia && (
        <i
          className="ph-fill ph-star-half"
          style={{ color: "#f59e0b", fontSize: 22 }}
        />
      )}
      {Array.from({ length: vazia }).map((_, i) => (
        <i
          key={`v${i}`}
          className="ph ph-star"
          style={{ color: "#d1d5db", fontSize: 22 }}
        />
      ))}
    </Box>
  );
}

function Variacao({ valor, sufixo = "%", label = "vs período anterior" }) {
  if (valor === null || valor === undefined) return null;
  const positivo = valor >= 0;
  const cor = positivo ? "#16a34a" : "#dc2626";
  const icone = positivo ? "ph-trend-up" : "ph-trend-down";
  return (
    <Box display="flex" alignItems="center" gap={0.5} mt={1.5}>
      <i className={`ph-bold ${icone}`} style={{ color: cor, fontSize: 16 }} />
      <Typography
        variant="body2"
        sx={{ color: cor, fontWeight: 700, lineHeight: 1 }}
      >
        {positivo ? "+" : ""}
        {valor % 1 === 0 ? valor : valor.toFixed(1)}
        {sufixo}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: "text.disabled", lineHeight: 1 }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function SecaoSatisfacao({ icone, corIcone, corFundo, titulo, children }) {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{ px: { xs: 0.5, md: 2 }, py: 1, height: "100%", width: "100%" }}
    >
      <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            backgroundColor: corFundo,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <i
            className={`ph-fill ${icone}`}
            style={{ color: corIcone, fontSize: 24 }}
          />
        </Box>
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: "text.secondary", lineHeight: 1.3 }}
        >
          {titulo}
        </Typography>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        width="100%"
      >
        {children}
      </Box>
    </Box>
  );
}

function MonitorOsSatisfacao({ filtrosBody }) {
  const [data, setData] = useState(defaultData);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    buscarSatisfacao();
  }, [filtrosBody]);

  async function buscarSatisfacao() {
    setLoading(true);
    const result = await ordemServicoService.getRelatorioDashboardSatisfacao(
      filtrosBody,
    );
    if (!result.isAxiosError) {
      setData(result.data || defaultData);
    } else {
      responseErros(result);
    }
    setLoading(false);
  }

  const notaFormatada = data.notaMedia
    ? data.notaMedia.toFixed(1).replace(".", ",")
    : "–";
  const indiceFormatado = data.indiceSatisfacao
    ? `${data.indiceSatisfacao.toFixed(1).replace(".", ",")}%`
    : "–";
  const variacaoNota = data.comparativo?.variacaoNota ?? null;
  const variacaoIndice = data.comparativo?.variacaoIndice ?? null;

  return (
    <Card
      sx={{
        borderRadius: 3,
        border: "1px solid #f0f0f0",
        height: "100%",
        pb: 4,
      }}
    >
      <CardContent sx={{ p: 2, height: "100%" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1.5}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Pesquisa de Satisfação
          </Typography>
          <i
            className="ph-bold ph-star"
            style={{ color: "#64748b", fontSize: 20 }}
          />
        </Box>
        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Grid container spacing={2}>
            {[1, 2, 3].map((i) => (
              <Grid item xs={4} md={12} key={i}>
                <Skeleton
                  variant="rounded"
                  height={120}
                  sx={{ borderRadius: 2 }}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={2} sx={{ height: "100%" }}>
            <Grid item xs={4} md={12}>
              <SecaoSatisfacao
                icone="ph-star"
                corIcone="#f59e0b"
                corFundo="#fef3c718"
                titulo="Nota Média"
              >
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, color: "#1e293b", lineHeight: 1.2 }}
                >
                  {notaFormatada}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.disabled", mt: 0.5, display: "block" }}
                >
                  de 5 estrelas
                </Typography>
                {data.notaMedia > 0 && <Estrelas nota={data.notaMedia} />}
                <Variacao
                  valor={variacaoNota}
                  sufixo="%"
                  label="vs período anterior"
                />
              </SecaoSatisfacao>
            </Grid>

            <Grid item xs={4} md={12}>
              <SecaoSatisfacao
                icone="ph-smiley"
                corIcone="#16a34a"
                corFundo="#dcfce718"
                titulo="Índice de Satisfação"
              >
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, color: "#1e293b", lineHeight: 1.2 }}
                >
                  {indiceFormatado}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.disabled", mt: 0.5, display: "block" }}
                >
                  clientes satisfeitos
                </Typography>
                <Box
                  sx={{
                    mt: 1.5,
                    height: 8,
                    backgroundColor: "#e2e8f0",
                    borderRadius: 3,
                    overflow: "hidden",
                    width: "100%",
                    maxWidth: 200,
                  }}
                >
                  <Box
                    sx={{
                      width: `${data.indiceSatisfacao || 0}%`,
                      height: "100%",
                      backgroundColor:
                        data.indiceSatisfacao >= 80
                          ? "#16a34a"
                          : data.indiceSatisfacao >= 60
                          ? "#f59e0b"
                          : "#dc2626",
                      borderRadius: 3,
                      transition: "width 0.5s ease",
                    }}
                  />
                </Box>
                <Variacao
                  valor={variacaoIndice}
                  sufixo=" p.p."
                  label="vs período anterior"
                />
              </SecaoSatisfacao>
            </Grid>

            <Grid item xs={4} md={12}>
              <SecaoSatisfacao
                icone="ph-clipboard-text"
                corIcone="#7c3aed"
                corFundo="#ede9fe18"
                titulo="O.S Respondidas"
              >
                <Box display="flex" alignItems="baseline" gap={0.75}>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 800, color: "#1e293b", lineHeight: 1.2 }}
                  >
                    {data.respondidas ?? "–"}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ color: "text.disabled", fontWeight: 500 }}
                  >
                    / {data.enviadas ?? "–"}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ color: "text.disabled", mt: 0.5, display: "block" }}
                >
                  respondidas / enviadas
                </Typography>
                {data.enviadas > 0 && (
                  <Box
                    sx={{
                      mt: 1.5,
                      height: 8,
                      backgroundColor: "#e2e8f0",
                      borderRadius: 3,
                      overflow: "hidden",
                      width: "100%",
                      maxWidth: 200,
                    }}
                  >
                    <Box
                      sx={{
                        width: `${Math.min(100, data.taxaResposta || 0)}%`,
                        height: "100%",
                        backgroundColor: "#7c3aed",
                        borderRadius: 3,
                        transition: "width 0.5s ease",
                      }}
                    />
                  </Box>
                )}
                <Typography
                  variant="body2"
                  sx={{ color: "text.disabled", mt: 0.5, display: "block" }}
                >
                  {data.taxaResposta > 0
                    ? `${data.taxaResposta
                        .toFixed(1)
                        .replace(".", ",")}% de resposta`
                    : "Nenhuma resposta ainda"}
                </Typography>
              </SecaoSatisfacao>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}

export default MonitorOsSatisfacao;
